import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { fetchShopCapabilities } from '../../../services/sui-service';
import { getShopByOwner, type Shop } from '../../../services/shop-service';

/**
 * Shared loader for the owner-settings sub-screens (StorefrontSettings,
 * RewardsSettings, CounterSettings, AccountSettings — Task 10-13).
 *
 * This is the LOAD half of ShopSettings.tsx's original single useEffect
 * (ShopSettings.tsx:85-147): resolve userAddress -> getShopByOwner ->
 * shopId/shopOnchainId/shopName, plus the full row for each screen to pull
 * its own section state from. The "no DB row yet, only an on-chain
 * capability" fallback is preserved so a shop that hasn't hit the backend
 * yet still resolves an id.
 *
 * Section-specific reads (e.g. getMyShopPrivate() for redemption_secret,
 * only relevant to the redemption-security section) are NOT here — each
 * sub-screen that needs one calls it itself.
 */
export function useShopSettingsLoad() {
    const { userAddress } = useAuthStore();
    const [loading, setLoading] = useState(true);
    const [shopId, setShopId] = useState<string | null>(null);
    const [shopOnchainId, setShopOnchainId] = useState<string | null>(null);
    const [shopName, setShopName] = useState('');
    const [shopRow, setShopRow] = useState<Shop | null>(null);

    const reload = useCallback(async () => {
        if (!userAddress) return;
        setLoading(true);
        try {
            const shop = await getShopByOwner(userAddress);
            if (shop) {
                setShopId(shop.id);
                setShopOnchainId(shop.onchain_id || null);
                setShopName(shop.name || 'My Shop');
                setShopRow(shop);
                setLoading(false);
                return;
            }

            const capabilities = await fetchShopCapabilities(userAddress);
            if (capabilities.length > 0) {
                const fields = (capabilities[0].data?.content as any)?.fields || {};
                const resolvedShopId = fields.shop_id || capabilities[0].data?.objectId;
                setShopId(resolvedShopId);
                setShopOnchainId(resolvedShopId);
                setShopName(fields.shop_name || 'My Shop');
                setShopRow(null);
            }
        } catch (err) {
            console.error('Failed to load shop:', err);
        }
        setLoading(false);
    }, [userAddress]);

    useEffect(() => {
        if (userAddress) reload();
    }, [userAddress, reload]);

    return { loading, shopId, shopOnchainId, shopName, shopRow, reload };
}
